import { useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ImagePlus, Loader2, X } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { trpc } from '@/lib/trpc';
import { cn } from '@/lib/utils';

const ALLOWED = ['image/png', 'image/jpeg', 'image/webp', 'image/gif'] as const;
type Allowed = (typeof ALLOWED)[number];

/**
 * Subida de imágenes de Learning (correcciones 07/09 §1 y §5): portada de las
 * cards de programas y avatar/portada del juego. Presigned PUT al
 * AttachmentsBucket (mismo patrón que el People Landing); guarda la KEY, la
 * vista se firma con getImageUrls.
 */
export function LearningImageField({
  folder,
  imageKey,
  onChange,
  aspect = 'wide',
  label,
}: {
  folder: 'programs' | 'arcade';
  imageKey?: string;
  onChange: (key: string | undefined) => void;
  aspect?: 'wide' | 'square';
  label?: string;
}) {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const [localPreview, setLocalPreview] = useState<string | null>(null);
  const getUpload = trpc.peopleLearning.getImageUploadUrl.useMutation();
  const urls = trpc.peopleLearning.getImageUrls.useQuery(
    { keys: imageKey ? [imageKey] : [] },
    { enabled: !!imageKey, staleTime: 55 * 60_000 }
  );
  const preview = localPreview ?? (imageKey ? urls.data?.urls[imageKey] : undefined);

  const pick = async (file: File) => {
    if (!ALLOWED.includes(file.type as Allowed)) {
      toast.error(t('people-learning.image.formato', 'Formato no permitido: usá PNG, JPG, WEBP o GIF'));
      return;
    }
    if (file.size > 8 * 1024 * 1024) {
      toast.error(t('people-learning.image.tamano', 'La imagen supera 8 MB'));
      return;
    }
    setUploading(true);
    try {
      const { uploadUrl, imageKey: key } = await getUpload.mutateAsync({ folder, contentType: file.type as Allowed });
      const res = await fetch(uploadUrl, { method: 'PUT', body: file, headers: { 'Content-Type': file.type } });
      if (!res.ok) throw new Error(`upload ${res.status}`);
      setLocalPreview(URL.createObjectURL(file));
      onChange(key);
    } catch (e) {
      toast.error(t('people-learning.image.error', 'No se pudo subir la imagen') + (e instanceof Error ? `: ${e.message}` : ''));
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="space-y-1.5">
      {label && <p className="text-xs font-medium text-muted-foreground">{label}</p>}
      <div
        className={cn(
          'relative overflow-hidden rounded-lg border border-dashed bg-muted/30 flex items-center justify-center',
          aspect === 'wide' ? 'aspect-16/7' : 'aspect-square w-32'
        )}
      >
        {preview ? (
          <img src={preview} alt="" className="h-full w-full object-cover" />
        ) : (
          <ImagePlus className="h-6 w-6 text-muted-foreground" />
        )}
        {uploading && (
          <div className="absolute inset-0 flex items-center justify-center bg-background/70">
            <Loader2 className="h-5 w-5 animate-spin" />
          </div>
        )}
      </div>
      <div className="flex gap-2">
        <Button type="button" size="sm" variant="outline" className="h-7 text-xs" disabled={uploading} onClick={() => inputRef.current?.click()}>
          <ImagePlus className="mr-1 h-3.5 w-3.5" />
          {imageKey ? t('people-learning.image.cambiar', 'Cambiar imagen') : t('people-learning.image.subir', 'Subir imagen')}
        </Button>
        {imageKey && (
          <Button type="button" size="sm" variant="ghost" className="h-7 text-xs text-destructive" onClick={() => { setLocalPreview(null); onChange(undefined); }}>
            <X className="mr-1 h-3.5 w-3.5" /> {t('people-learning.image.quitar', 'Quitar')}
          </Button>
        )}
        <input
          ref={inputRef}
          type="file"
          accept={ALLOWED.join(',')}
          className="hidden"
          onChange={(e) => { const f = e.target.files?.[0]; if (f) void pick(f); e.target.value = ''; }}
        />
      </div>
    </div>
  );
}
